import { type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

/**
 * The one status pill for the whole portal.
 *
 * Every plugin had grown its own `severityChipStyle` / `statusTone` / `runTone`
 * helper returning `{ backgroundColor, color }` hex pairs, which meant four
 * different greens, no dark mode, and a fix in one page reaching only that page
 * (docs/ux-design-review.md §1b). `tone` maps onto the `--x-subtle` /
 * `--x-strong` token pairs, so a badge is readable in both themes by
 * construction.
 *
 * Prefer `Badge` when you want a solid, high-emphasis fill; `StatusBadge` is the
 * tinted, scannable variant used in tables and lists.
 */
const statusBadgeVariants = cva(
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium',
  {
    variants: {
      tone: {
        ok: 'bg-success-subtle text-success-strong',
        warn: 'bg-warning-subtle text-warning-strong',
        danger: 'bg-destructive-subtle text-destructive-strong',
        neutral: 'bg-neutral-subtle text-neutral-strong',
      },
    },
    defaultVariants: {
      tone: 'neutral',
    },
  },
);

/** The semantic buckets every status in the portal collapses into. */
export type StatusTone = 'ok' | 'warn' | 'danger' | 'neutral';

export interface StatusBadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof statusBadgeVariants> {}

export const StatusBadge = ({
  className,
  tone,
  ...props
}: StatusBadgeProps) => (
  <span className={cn(statusBadgeVariants({ tone }), className)} {...props} />
);

/**
 * Maps the severity vocabularies used across the agent outputs onto a tone.
 * Unknown values fall back to `neutral` rather than silently reading as "ok".
 */
export const severityTone = (
  severity: string | undefined | null,
): StatusTone => {
  switch (severity?.toLowerCase()) {
    case 'critical':
    case 'high':
    case 'fail':
    case 'failed':
    case 'failure':
    case 'error':
    case 'blocked':
    case 'non-compliant':
      return 'danger';
    case 'warning':
    case 'warn':
    case 'medium':
    case 'moderate':
    case 'conditional-pass':
    case 'conditional pass':
    case 'in-progress':
    case 'in progress':
    case 'pending':
    case 'partial':
      return 'warn';
    case 'pass':
    case 'passed':
    case 'ok':
    case 'success':
    case 'succeeded':
    case 'healthy':
    case 'compliant':
    case 'low':
    case 'approved':
    case 'completed':
      return 'ok';
    default:
      return 'neutral';
  }
};

/**
 * Text and dot classes for a tone used outside a badge — legend swatches,
 * inline counts, accent borders. `-strong` is the text-safe member of each
 * pair; the dot and border classes use the solid fill, which is correct for a
 * non-text element.
 */
export const TONE_TEXT_CLASS: Record<StatusTone, string> = {
  ok: 'text-success-strong',
  warn: 'text-warning-strong',
  danger: 'text-destructive-strong',
  neutral: 'text-neutral-strong',
};

export const TONE_DOT_CLASS: Record<StatusTone, string> = {
  ok: 'bg-success',
  warn: 'bg-warning',
  danger: 'bg-destructive',
  neutral: 'bg-neutral-strong',
};

/**
 * The one status dot beside a label: a step, a check, a section. Decorative,
 * so the label beside it (or screen-reader text) must say the status too.
 */
export const StatusDot = ({
  tone = 'neutral',
  className,
  title,
}: {
  tone?: StatusTone;
  className?: string;
  title?: string;
}) => (
  <span
    aria-hidden="true"
    title={title}
    className={cn('size-2 shrink-0 rounded-full', TONE_DOT_CLASS[tone], className)}
  />
);

export const TONE_BORDER_CLASS: Record<StatusTone, string> = {
  ok: 'border-l-success',
  warn: 'border-l-warning',
  danger: 'border-l-destructive',
  neutral: 'border-l-border',
};

export { statusBadgeVariants };
